export interface IFilters {
    searchString: string;
    applyFilters: boolean; // if false, only apply search
    [key: string]: IFilter[] | any;
}

export interface IFilter {
    id: string;
    icon?: React.ReactNode;
    name: string;
    value: boolean;
}

export const filterAll = <T extends Record<string, any>>(filters: IFilters, objects: T[]): T[] => {
    const { searchString, applyFilters = true, ...filterGroups } = filters;

    return objects.filter((obj) => {
        // 1) Search (case-insensitive across string fields)
        if (searchString?.trim()) {
            const q = searchString.toLowerCase();
            const matchesSearch = Object.values(obj).some((v) => typeof v === "string" && v.toLowerCase().includes(q));
            if (!matchesSearch) return false;
        }

        // 2) Optional filter groups
        if (applyFilters) {
            for (const [key, group] of Object.entries(filterGroups)) {
                if (!Array.isArray(group)) continue; // not a filter array, skip

                const active = (group as IFilter[]).filter((f) => f.value).map((f) => f.id);
                if (active.length === 0) continue; // nothing selected for this group

                const field = (obj as any)[key];
                // Support scalar or array fields on the object
                const matches = Array.isArray(field)
                    ? field.some((v: any) => active.includes(String(v)))
                    : active.includes(String(field));

                if (!matches) return false;
            }
        }

        return true;
    });
};

export const findAllFilters = (filters: IFilter[]): { value: string; additionalSelected: number } => {
    let firstValue = "";
    let totalSelected = 0;

    for (const f of filters) {
        if (f.value) {
            totalSelected++;
            if (!firstValue) {
                firstValue = f.name; // or f.id if preferred
            }
        }
    }

    // If nothing is selected → "all"
    if (totalSelected === 0) {
        return { value: "Filtering.all", additionalSelected: 0 };
    }

    return {
        value: firstValue,
        additionalSelected: totalSelected - 1,
    };
};

export const emptyAllFitlers = (filters: IFilters): IFilters => {
    const newFilters: IFilters = { ...filters, searchString: "", applyFilters: true };

    for (const key in filters) {
        if (Array.isArray(filters[key])) {
            newFilters[key] = (filters[key] as IFilter[]).map((f) => ({ ...f, value: false }));
        }
    }

    return newFilters;
};

export const hasAnyActiveFilter = (filters: IFilters): boolean => {
    for (const key in filters) {
        if (Array.isArray(filters[key])) {
            if ((filters[key] as IFilter[]).some((f) => f.value)) {
                return true;
            }
        }
    }
    return false;
};
